import { jsx, css } from '@emotion/react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { forwardRef, useState } from 'react';

import { useAccessibleInteractivity } from '../../../hooks';
import Theme from '../../../themes/index';

const RadioButtonLabel = styled.label`
  color: ${props => (props.disabled ? '#666' : 'black')};
  display: flex;
  flex-direction: ${props => (props.labelLeft ? 'row-reverse' : 'row')};
  justify-content: ${props => (props.labelLeft ? 'space-between' : 'flex-start')};
  opacity: ${props => (props.disabled ? '.5' : '1')};
  position: relative;

  &:hover {
    cursor: ${props => (props.disabled ? 'default' : 'pointer')};
  }

  &:focus {
    outline-offset: ${props => (props.disabled ? '0' : '2px')};
    outline: ${props => (props.disabled ? 'none' : `${props.themeColor} solid 2px`)};
  }

  &:focus:not(:focus-visible) {
    outline: none !important;
  }

  &:focus-visible {
    outline-offset: ${props => (props.disabled ? '0' : '2px')};
    outline: ${props => (props.disabled ? 'none' : `${props.themeColor} solid 2px`)};
  }

  input[type='radio']:checked {
    background-color: white;
    border-color: ${props => (props.disabled ? '#666666' : props.themeColor)};
  }

  input[type='radio'] {
    -moz-appearance: none;
    -webkit-appearance: none;
    background-color: #ffffff;
    border-radius: 8px;
    border: 1px solid #d1d1d1;
    cursor: ${props => (props.disabled ? 'default' : 'pointer')};
    flex-shrink: 0;
    height: 16px;
    margin: 2px 8px 0 0;
    width: 16px;
  }

  input[type='radio'] ~ .radio {
    background: ${props => props.themeColor};
    border-radius: 6px;
    display: none;
    height: 8px;
    left: ${props => (props.labelLeft ? 'auto' : '4px')};
    position: absolute;
    top: 6px;
    width: 8px;

    ${props =>
      props.labelLeft === true &&
      `
      right: 12px;
    `}
  }

  input[type='radio']:checked ~ .radio {
    display: inline-block;
  }
`;

const ControlledRadioButton = forwardRef(
  (
    {
      children,
      disabled = false,
      labelLeft = false,
      name,
      themeColor = Theme.colors.brand,
      onChange,
      value,
      checked,
      ...props
    },
    ref
  ) => (
    <RadioButtonLabel
      className="radio-label"
      disabled={disabled}
      labelLeft={labelLeft}
      themeColor={themeColor}
      role="button"
      tabIndex="-1"
    >
      <input
        checked={checked}
        disabled={disabled}
        name={name}
        onChange={onChange}
        readOnly={disabled ? 'readonly' : null}
        ref={ref}
        tabIndex={disabled ? '-1' : '0'}
        type="radio"
        value={value}
        {...props}
      />
      {children}
      <i aria-checked={checked} aria-disabled={disabled} className="radio" role="checkbox" />
    </RadioButtonLabel>
  )
);

const UncontrolledRadioButton = forwardRef(({ defaultChecked, onChange, ...props }, ref) => {
  const [checked, setChecked] = useState(defaultChecked);

  const updatedChecked = event => {
    setChecked(!checked);
    {
      onChange && onChange(event);
    }
  };

  return <ControlledRadioButton checked={checked} onChange={updatedChecked} ref={ref} {...props} />;
});

const RadioButton = forwardRef((props, ref) => {
  const isControlled = props.checked !== undefined && props.checked !== null;
  const [renderedControlled] = useState(isControlled);
  if (renderedControlled !== isControlled) {
    console.warn('A RadioButton is changing from controlled to uncontrolled. This is likely a bug.');
  }

  if (isControlled) {
    return <ControlledRadioButton {...props} ref={ref} />;
  }

  return <UncontrolledRadioButton {...props} ref={ref} />;
});

export default RadioButton;

RadioButton.propTypes = {
  /** This is where you put the label of the checkbox */
  children: PropTypes.node,
  /** Set this to true, if you want the checkbox disabled */
  disabled: PropTypes.bool,
  /** Set a name for the radio group */
  name: PropTypes.string,
  /** Set a value for the radio */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Call the function you want to fire when changed */
  onChange: PropTypes.func,
  /** If you want the checkbox checked on load. ONLY use if you want an UNcontrolled RadioButton */
  defaultChecked: PropTypes.bool,
  /** If the radio should be checked on any given render. Use if you want a controlled RadioButton */
  checked: PropTypes.bool,
  /** If you want the label to be left and the check to the right */
  labelLeft: PropTypes.bool,
  /** Hex value for color theme */
  themeColor: PropTypes.string,
};
